import clsx from "clsx";
import { Fragment, MutableRefObject, useContext } from "react";
import { Trash2 } from "lucide-react";
import Button from "../button";
import { SelectGroup, SelectOption } from "../control/types";
import { DropdownContext, DropdownItem } from "../dropdown";
import { Options, OptionsOrGroups } from "./types";

interface SelectMenuProps<
  Option extends SelectOption = SelectOption,
  Group extends SelectGroup<Option> = SelectGroup<Option>,
> {
  options: OptionsOrGroups<Option, Group>;
  selectedValues: Options<Option>;
  isCreateEnabled?: boolean;
  onDelete?: (option: Option) => void;
  isOptionDeletable?: (option: Option) => boolean;
  isOptionDisabled?: (option: Option, selectValue: Options<Option>) => boolean;
}

interface SelectMenuItemProps<
  Option extends SelectOption = SelectOption,
  Group extends SelectGroup<Option> = SelectGroup<Option>,
> extends SelectMenuProps<Option, Group> {
  option: Option;
  index: number;
  activeIndex?: number | null;
  listRef: MutableRefObject<(HTMLElement | null)[]>;
  getItemProps: (
    userProps?: React.HTMLProps<HTMLElement>
  ) => Record<string, unknown>;
}

const SelectMenuItem = <
  Option extends SelectOption = SelectOption,
  Group extends SelectGroup<Option> = SelectGroup<Option>,
>(
  props: SelectMenuItemProps<Option, Group>
) => {
  const {
    selectedValues,
    option,
    activeIndex,
    index,
    listRef,
    getItemProps,
    isOptionDisabled,
    isOptionDeletable = () => true,
    onDelete,
  } = props;

  const optionDisabled = isOptionDisabled?.(option, selectedValues);
  const selected = !!selectedValues.find((value) => value.id === option.id);
  const setRef = (node: HTMLElement | null) => {
    if (index != null && index >= 0 && !optionDisabled)
      listRef.current[index] = node;
  };

  const isDeletable = onDelete && isOptionDeletable(option);

  return (
    <DropdownItem
      {...getItemProps({
        ref: setRef,
      })}
      eventKey={option.id}
      selected={selected}
      disabled={optionDisabled}
      active={activeIndex === index}
      className={clsx({
        "flex flex-row gap-1 items-center py-1": isDeletable,
      })}
    >
      <span className="flex-1">{option.label}</span>
      {isDeletable && (
        <Button
          variant="light"
          mode="flat"
          size="sm"
          className="bg-transparent"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(option);
          }}
        >
          <Trash2 size="14" />
        </Button>
      )}
    </DropdownItem>
  );
};

const SelectMenu = <
  Option extends SelectOption = SelectOption,
  Group extends SelectGroup<Option> = SelectGroup<Option>,
>(
  props: SelectMenuProps<Option, Group>
) => {
  const { options, isCreateEnabled } = props;
  const { activeIndex, listRef, getItemProps } = useContext(DropdownContext);

  const isEmpty = !options.length;
  const flat = options
    .flatMap((opt) => ("options" in opt ? opt.options : opt))
    .map((x) => x.id);

  return (
    <>
      {isEmpty && (
        <div className="transition-all px-6 py-2 text-sm text-center">
          <span>No Options</span>
          {isCreateEnabled && <span> - Press Enter to Create</span>}
        </div>
      )}
      {options.map((option) => {
        if ("options" in option) {
          return (
            <Fragment key={option.label}>
              <div className="text-xs text-gunmetal-500 px-3 pt-2">
                {option.label}
              </div>
              {option.options.map((subOption) => (
                <SelectMenuItem<Option, Group>
                  {...props}
                  key={subOption.id}
                  option={subOption}
                  index={flat.findIndex((value) => value === subOption.id)}
                  activeIndex={activeIndex}
                  listRef={listRef}
                  getItemProps={getItemProps}
                />
              ))}
            </Fragment>
          );
        }

        return (
          <SelectMenuItem<Option, Group>
            {...props}
            key={option.id}
            option={option}
            index={flat.findIndex((value) => value === option.id)}
            activeIndex={activeIndex}
            listRef={listRef}
            getItemProps={getItemProps}
          />
        );
      })}
    </>
  );
};

export default SelectMenu;
